import React, { useState, useEffect } from 'react';
import { X, ClipboardList } from 'lucide-react';
import { api } from '../../utils/api';
import RecordKeeperPanel from '../admin/RecordKeeperPanel';

/**
 * MyDiaryModal — Driver's own diary editor.
 * Wraps the full RecordKeeperPanel with the driver locked to themselves.
 * Same editing experience as admin/record-keeper: timeline, insert, adjust, upload, audit log.
 */
const MyDiaryModal = ({ isOpen, onClose, driverId, driverName }) => {
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && driverId) {
      loadData();
    }
  }, [isOpen, driverId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [driverRes, vehicleRes] = await Promise.all([
        api('/drivers/me').catch(() => null),
        api('/vehicles').catch(() => ({ vehicles: [] })),
      ]);

      // Build a driver record matching the format RecordKeeperPanel expects
      const nameParts = (driverName || '').split(' ');
      const driverRecord = {
        driver_id: driverId,
        vehicle_id: driverRes?.preferred_vehicle_id || null,
        users: {
          first_name: nameParts[0] || '',
          last_name: nameParts.slice(1).join(' ') || '',
        },
      };
      setDrivers([driverRecord]);
      setVehicles(vehicleRes?.vehicles || []);
    } catch (err) {
      console.error('Failed to load diary data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-4xl border border-slate-700 shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">My Diary</h3>
              <p className="text-sm text-slate-400">View &amp; Edit Entries</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition"
            data-testid="my-diary-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content — full RecordKeeperPanel */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-slate-600 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : (
            <RecordKeeperPanel
              drivers={drivers}
              vehicles={vehicles}
              lockedDriverId={driverId}
            />
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700 shrink-0">
          <p className="text-xs text-slate-500 text-center">
            All changes are logged for compliance auditing
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyDiaryModal;
